/**
 * 한글 파일 이벤트(시험지 /events/hwp-exam · 생기부 교과세특 /events/hwp-record) 성취기준 선택 도우미 — 순수 함수만.
 *
 * 이벤트 페이지의 학년 칩(e5·m2·h1)/학교급 칩(elementary·middle·high)과 과목 라벨(수학·국어)을
 * 2022 개정 교육과정 카탈로그의 subject_id(elementary-수학-5-6, middle-과학, high-공통수학1)로 이어 주고,
 * 고른 성취기준을 단원/주제 문장·서버 페이로드로 바꾼다.
 * 카탈로그 스키마는 backend/routers/curriculum_standards.py 의 GET /api/curriculum/standards/catalog 와 같다.
 *
 * 범위(scope) = { level: 'elementary'|'middle'|'high', gradeBand: '3-4'|'5-6'|null, gradeNumber: 1~6|null }
 *  - 시험지: 학년 칩 → resolveExamGradeScope (초등은 학년군까지, 고등은 학년으로 공통/일반선택 우선순위)
 *  - 생기부: 학교급 칩 → resolveRecordScope (학년 없음 → 초등은 학년군을 세부 과목 칩으로 고른다)
 */

export const MAX_EXAM_STANDARDS = 5;
export const RECOMMENDED_EXAM_STANDARDS = 3;
export const MAX_RECORD_STANDARDS = 3;
export const RECOMMENDED_RECORD_STANDARDS = 2;
export const EXAM_TOPIC_MAX_CHARS = 120;
export const STANDARD_TEXT_MAX_CHARS = 200;
export const STANDARD_CHIP_MAX_CHARS = 30;

/** 이벤트 과목 id → 카탈로그 form_subject_map 의 과목 라벨. */
export const EXAM_SUBJECT_LABELS = Object.freeze({
  korean: '국어',
  math: '수학',
  social: '사회',
  science: '과학',
  english: '영어',
});

/**
 * 생기부 교과세특 과목 칩 순서. 카탈로그 form_subject_map 라벨 기준이며, 여기 없는 라벨은 뒤에 카탈로그 순서로 붙는다.
 * 화면 라벨이 다른 것(기가정보 → 기술·가정/정보)은 RECORD_SUBJECT_DISPLAY 로 바꾼다.
 */
export const RECORD_SUBJECT_ORDER = Object.freeze([
  '국어', '수학', '사회', '과학', '영어', '도덕', '역사', '지리', '음악', '미술', '체육', '기가정보', '한문', '제2외국어',
]);
export const RECORD_SUBJECT_DISPLAY = Object.freeze({ '기가정보': '기술·가정/정보' });

/** 고교 선택 과목 중 이벤트에 노출하는 이수 구분(공통·일반선택). 진로·융합·전문교과는 제외. */
const HIGH_COURSE_CATEGORIES = Object.freeze(['common', 'general']);
const SCHOOL_LEVELS = Object.freeze(['elementary', 'middle', 'high']);

/**
 * 학년 칩 id → 학교급·학년군. 초등은 3-4/5-6 학년군, 중·고는 학교급 하나로 묶인다.
 * 알 수 없는 값이면 null.
 */
export const resolveExamGradeScope = (gradeId) => {
  const match = /^([emh])([1-6])$/.exec(String(gradeId || '').trim());
  if (!match) return null;
  const [, prefix, digit] = match;
  const gradeNumber = Number(digit);
  if (prefix === 'e') {
    if (gradeNumber < 3) return null; // 이벤트 학년 칩은 초3부터
    return { level: 'elementary', gradeBand: gradeNumber <= 4 ? '3-4' : '5-6', gradeNumber };
  }
  if (gradeNumber > 3) return null;
  return { level: prefix === 'm' ? 'middle' : 'high', gradeBand: null, gradeNumber };
};

/** 생기부 학교급 칩(elementary·middle·high) → 학년 없는 범위. 알 수 없는 값이면 null. */
export const resolveRecordScope = (schoolLevel) => {
  const level = String(schoolLevel || '').trim();
  if (!SCHOOL_LEVELS.includes(level)) return null;
  return { level, gradeBand: null, gradeNumber: null };
};

/** 카탈로그의 school_levels → subject_groups → subjects 를 id 사전으로 펴고 school_level 을 붙인다. */
export const indexCatalogSubjects = (catalog) => {
  const byId = {};
  (catalog?.school_levels || []).forEach((level) => {
    (level?.subject_groups || []).forEach((group) => {
      (group?.subjects || []).forEach((subject) => {
        if (!subject?.id) return;
        byId[subject.id] = {
          ...subject,
          school_level: subject.school_level || level.id,
          subject_group: subject.subject_group || group.label || group.id,
        };
      });
    });
  });
  return byId;
};

const compareGradeBandDesc = (a, b) => String(b?.grade_band || '').localeCompare(String(a?.grade_band || ''));

/**
 * 범위(scope)·과목 라벨에 맞는 카탈로그 과목 후보. 첫 항목이 기본 선택이다.
 * - 초등: 학년군이 있으면 그 학년군 1개(elementary-수학-5-6), 없으면(생기부) 높은 학년군부터 전부
 * - 중등: middle-과목 1개
 * - 고등: 공통·일반선택만. 고1·학년 없음은 공통(공통수학1 …) 우선, 고2·3은 일반선택(대수·미적분Ⅰ …) 우선
 * 카탈로그가 없거나 매칭이 없으면 [] → 호출부는 기존 칩 입력으로 폴백한다.
 */
export const resolveSubjectCandidates = (catalog, scope, subjectLabel) => {
  const label = String(subjectLabel || '').trim();
  if (!scope || !label || !catalog) return [];
  const ids = catalog.form_subject_map?.[label];
  if (!Array.isArray(ids) || ids.length === 0) return [];
  const byId = indexCatalogSubjects(catalog);
  const metas = ids
    .map((id) => byId[id])
    .filter((meta) => meta && meta.school_level === scope.level);
  if (scope.level === 'elementary') {
    if (scope.gradeBand) return metas.filter((meta) => meta.grade_band === scope.gradeBand);
    return [...metas].sort(compareGradeBandDesc);
  }
  if (scope.level === 'middle') return metas;
  const common = metas.filter((meta) => meta.course_category === 'common');
  const general = metas.filter((meta) => meta.course_category === 'general');
  const generalFirst = Number(scope.gradeNumber) >= 2;
  const ordered = generalFirst ? [...general, ...common] : [...common, ...general];
  return ordered.filter((meta) => HIGH_COURSE_CATEGORIES.includes(meta.course_category));
};

/** 시험지: 학년 칩 id + 이벤트 과목 id(math) 로 후보를 구한다. */
export const resolveExamSubjectCandidates = (catalog, gradeId, subjectId) => (
  resolveSubjectCandidates(catalog, resolveExamGradeScope(gradeId), EXAM_SUBJECT_LABELS[subjectId])
);

/**
 * 생기부 교과세특 과목 칩 목록: 카탈로그 form_subject_map 라벨 중 이 학교급에 후보가 있는 것만,
 * RECORD_SUBJECT_ORDER 순서(나머지는 카탈로그 순서). 각 항목은 { id: 라벨(서버 subject_or_club 값), label: 표시 }.
 * 카탈로그가 없으면 [] → 호출부는 기존 과목 칩으로 폴백한다.
 */
export const resolveRecordSubjectOptions = (catalog, scope) => {
  if (!catalog || !scope) return [];
  const labels = Object.keys(catalog.form_subject_map || {});
  const ordered = [
    ...RECORD_SUBJECT_ORDER.filter((label) => labels.includes(label)),
    ...labels.filter((label) => !RECORD_SUBJECT_ORDER.includes(label)),
  ];
  return ordered
    .filter((label) => resolveSubjectCandidates(catalog, scope, label).length > 0)
    .map((label) => ({ id: RECORD_SUBJECT_DISPLAY[label] || label, label: RECORD_SUBJECT_DISPLAY[label] || label, catalogLabel: label }));
};

/** 화면 라벨(기술·가정/정보) → 카탈로그 form_subject_map 라벨(기가정보). 나머지는 그대로. */
export const recordCatalogSubjectLabel = (value) => {
  const trimmed = String(value || '').trim();
  const found = Object.entries(RECORD_SUBJECT_DISPLAY).find(([, display]) => display === trimmed);
  return found ? found[0] : trimmed;
};

/** 세부 과목 칩 라벨. 초등처럼 같은 이름이 학년군별로 있으면 `수학 5~6학년` 으로 구분한다. */
export const candidateChipLabel = (meta, candidates = []) => {
  const label = String(meta?.label || meta?.id || '');
  const band = String(meta?.grade_band || '');
  const duplicated = candidates.filter((item) => item?.label === meta?.label).length > 1;
  if (!band || !duplicated) return label;
  return `${label} ${band.replace('-', '~')}학년`;
};

const collapseSpaces = (value) => String(value || '').replace(/\s+/g, ' ').trim();

/** 성취기준 원문에서 칩에 보여줄 짧은 문장. 마지막 마침표를 떼고 길면 말줄임. 코드는 붙이지 않는다. */
export const summarizeStandardText = (text, maxChars = STANDARD_CHIP_MAX_CHARS) => {
  const cleaned = collapseSpaces(text).replace(/^\[[^\]]*\]\s*/, '').replace(/\.$/, '');
  if (cleaned.length <= maxChars) return cleaned;
  return `${cleaned.slice(0, maxChars - 1).trimEnd()}…`;
};

/** 서버 응답 domains[].standards[] 를 화면용 [{key, code, text, domain, subjectId}] 로 평탄화한다. */
export const flattenSubjectStandards = (payload) => {
  const subjectId = payload?.subject?.id || '';
  const result = [];
  (payload?.domains || []).forEach((domain) => {
    const domainLabel = domain?.domain || '';
    (domain?.standards || []).forEach((standard) => {
      if (!standard?.code) return;
      result.push({
        key: standard.key || `${subjectId}:${standard.code}`,
        code: standard.code,
        text: collapseSpaces(standard.text),
        domain: domainLabel,
        subjectId,
      });
    });
  });
  return result;
};

/**
 * 고른 성취기준으로 단원/주제 문장을 만든다: `영역 - 요약1, 요약2` (영역이 하나일 때만 접두).
 * 백엔드 topic 상한(120자)에 맞춰 자른다.
 */
export const buildTopicFromStandards = (standards, maxChars = EXAM_TOPIC_MAX_CHARS) => {
  const list = (standards || []).filter((item) => item && item.text);
  if (list.length === 0) return '';
  const domains = [...new Set(list.map((item) => item.domain).filter(Boolean))];
  const prefix = domains.length === 1 ? `${domains[0]} - ` : '';
  const body = list.map((item) => summarizeStandardText(item.text, 40)).join(', ');
  const topic = `${prefix}${body}`;
  if (topic.length <= maxChars) return topic;
  return `${topic.slice(0, maxChars - 1).trimEnd()}…`;
};

/** 서버 fields.achievement_standards 페이로드. 최대 max(시험지 5·생기부 3)개, code 중복 제거, text 200자. */
export const toAchievementStandardsPayload = (standards, max = MAX_EXAM_STANDARDS) => {
  const seen = new Set();
  const payload = [];
  (standards || []).forEach((item) => {
    const code = collapseSpaces(item?.code);
    if (!code || seen.has(code)) return;
    seen.add(code);
    const entry = { code, text: collapseSpaces(item.text).slice(0, STANDARD_TEXT_MAX_CHARS) };
    const key = collapseSpaces(item.key);
    if (key) entry.key = key;
    payload.push(entry);
  });
  return payload.slice(0, max);
};

/** 선택 토글. 이미 있으면 빼고, 상한이면 그대로 둔다. */
export const toggleStandardSelection = (selected, standard, max = MAX_EXAM_STANDARDS) => {
  const list = selected || [];
  const exists = list.some((item) => item.key === standard.key);
  if (exists) return list.filter((item) => item.key !== standard.key);
  if (list.length >= max) return list;
  return [...list, standard];
};
